import { Css, useTestIds } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { createDesignPackageUrl, createPlanPackageUrl } from "src/RouteUrls";
import { ReadyPlanAssetType, SaveProductOfferingInput } from "src/generated/graphqlTypes";
import { ReadyOnlyOptionsTable } from "src/routes/developments/product-offerings/components/ReadOnlyOptionsTable";
import { PageHeader } from "src/routes/layout/PageHeader";
import { PLAN_FALLBACK_IMG } from "src/routes/libraries/plan-package/takeoffs/utils";
import {
  ReviewStepQuery,
  useReviewStepQuery,
} from "src/routes/libraries/product-offerings/add-product-offering/components/5.ReviewStep.generated";
import { queryResult } from "src/utils";
import { openNewTab } from "src/utils/window";
import { ProductOfferingStepActions } from "./ProductOfferingStepActions";

type ReviewStepProps = {
  formState: ObjectState<Partial<SaveProductOfferingInput>>;
  planPackage: ReviewStepQuery["planPackage"];
  exteriorDesignPackage: ReviewStepQuery["exteriorDesignPackage"];
  interiorDesignPackage: ReviewStepQuery["interiorDesignPackage"];
  rpos: ReviewStepQuery["readyPlanOptions"];
};
export function ReviewStep({ formState }: { formState: ReviewStepProps["formState"] }) {
  const query = useReviewStepQuery({
    variables: {
      planPackageId: formState.planPackageId.value || "",
      exteriorDesignPackageId: formState.exteriorDesignPackageId.value || "",
      interiorDesignPackageId: formState.interiorDesignPackageId.value || "",
      readyPlanOptionsFilter: { id: formState.options.value.map((rpo) => rpo.id) },
    },
  });
  return queryResult(query, ({ planPackage, exteriorDesignPackage, interiorDesignPackage, readyPlanOptions }) => (
    <ReviewStepView
      formState={formState}
      planPackage={planPackage}
      exteriorDesignPackage={exteriorDesignPackage}
      interiorDesignPackage={interiorDesignPackage}
      rpos={readyPlanOptions}
    />
  ));
}

function ReviewStepView(props: ReviewStepProps) {
  const { planPackage, exteriorDesignPackage, interiorDesignPackage, formState, rpos } = props;
  const tid = useTestIds({});
  const elevationOptions = rpos.filter((rpo) => rpo.active && rpo.type.isElevation);
  const coverPhotoUrl =
    planPackage.assets?.find((asset) => asset?.type.code === ReadyPlanAssetType.CoverPhoto)?.asset.planPackagePreview ??
    PLAN_FALLBACK_IMG;

  return (
    <div>
      <PageHeader
        title={formState.name.value || ""}
        left={
          <div css={Css.df.fdc.gap1.ml("150px").$}>
            <div css={Css.xl2.$}>{planPackage.name}</div>
            <div
              css={Css.md.blue600.onHover.cursorPointer.$}
              onClick={() => openNewTab(createPlanPackageUrl(planPackage.id, planPackage.version.id))}
            >
              {planPackage.displayCode}
            </div>
          </div>
        }
        titleSize="xl2"
      />
      <div css={Css.bshBasic.br12.df.fdr.bgWhite.$} {...tid.imageAsset}>
        <img src={coverPhotoUrl} alt="Product Offering cover photo" {...tid.coverPhoto} css={Css.w("38%").ha.$} />

        <div css={Css.ml8.pt8.$}>
          <div css={Css.xl2.$}>Key Specs</div>
          <div css={Css.pt2.$}>
            <div css={Css.df.fdc.gap1.$}>
              <div css={Css.mdSb.$}>Elevations</div>
              {elevationOptions?.map((rpo) => (
                <div key={rpo.id} css={Css.sm.$}>
                  {rpo.displayName}
                </div>
              ))}
            </div>
          </div>

          <div css={Css.pt4.pb8.$}>
            <div css={Css.mb1.mdSb.$}>Linked Data</div>
            <div css={Css.gap3.df.fdr.sm.$}>
              <div
                {...tid.planPackageName}
                css={Css.sm.blue600.onHover.cursorPointer.$}
                onClick={() => openNewTab(createPlanPackageUrl(planPackage.id, planPackage.version.id))}
              >
                {planPackage?.name}
              </div>
              <div
                {...tid.exteriorDesignPackageName}
                css={Css.sm.blue600.onHover.cursorPointer.$}
                onClick={() =>
                  openNewTab(createDesignPackageUrl(exteriorDesignPackage.id, exteriorDesignPackage.version.id))
                }
              >
                {exteriorDesignPackage?.name}
              </div>
              <div
                {...tid.interiorDesignPackageName}
                css={Css.sm.blue600.onHover.cursorPointer.$}
                onClick={() =>
                  openNewTab(createDesignPackageUrl(interiorDesignPackage.id, interiorDesignPackage.version?.id ?? ""))
                }
              >
                {interiorDesignPackage?.name}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div css={Css.w100.bgWhite.p4.my6.$}>
        <ReadyOnlyOptionsTable readyPlanOptions={rpos} />
      </div>
      <ProductOfferingStepActions formState={formState} />
    </div>
  );
}
